import React from 'react';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/Card';

export interface StatCardProps {
  /** The main label/title for the statistic */
  label: string;
  /** The primary value to display (number, string, or JSX element) */
  value: React.ReactNode;
  /** Optional icon to display on the right side */
  icon?: React.ReactNode;
  /** Optional trend text (e.g., "+12% from last month") */
  trend?: string;
  /** Color variant for the value text */
  valueColor?: 'default' | 'success' | 'warning' | 'error' | 'muted';
  /** Color variant for the icon background */
  iconColor?: 'default' | 'primary' | 'success' | 'warning' | 'error' | 'muted';
  /** Additional CSS classes */
  className?: string;
  /** Optional click handler to make the card interactive */
  onClick?: () => void;
  /** Whether the card should show loading state */
  loading?: boolean;
}

const valueColorClasses = {
  default: 'text-foreground',
  success: 'text-green-600',
  warning: 'text-amber-600',
  error: 'text-red-600',
  muted: 'text-muted-foreground'
};

const iconColorClasses = {
  default: 'bg-muted text-muted-foreground',
  primary: 'bg-primary-100 text-primary-600',
  success: 'bg-green-100 text-green-600',
  warning: 'bg-amber-100 text-amber-600',
  error: 'bg-red-100 text-red-600',
  muted: 'bg-muted/50 text-muted-foreground'
};

/**
 * StatCard - A standardized component for displaying key statistics
 * 
 * Features:
 * - Consistent padding and spacing
 * - Responsive design
 * - Optional icon with themed colors
 * - Trend indicators
 * - Loading states
 * - Interactive capability
 */
export function StatCard({
  label,
  value,
  icon,
  trend,
  valueColor = 'default',
  iconColor = 'default', 
  className,
  onClick,
  loading = false
}: StatCardProps) {
  return (
    <Card 
      className={cn(
        'transition-all duration-200',
        onClick && 'cursor-pointer hover:shadow-md hover:border-border',
        className
      )}
      onClick={onClick}
    >
      <CardContent className="py-0 sm:py-4">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-muted-foreground mb-1">
              {label}
            </p>
            
            {loading ? (
              <div className="w-16 h-8 bg-muted rounded animate-pulse" />
            ) : (
              <p className={cn(
                'text-2xl font-bold leading-none',
                valueColorClasses[valueColor]
              )}>
                {value}
              </p>
            )}
            
            {trend && !loading && (
              <p className="text-xs text-muted-foreground mt-1">
                {trend}
              </p>
            )}
          </div>
          
          {icon && (
            <div className={cn(
              'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ml-4',
              iconColorClasses[iconColor]
            )}>
              {icon}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}